import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { Button } from '../ui/button';

interface VaultModalProps {
  isOpen: boolean;
  onClose: () => void;
  appId: string;
}

export const VaultModal: React.FC<VaultModalProps> = ({ isOpen, onClose, appId }) => {
  const [secretKeys, setSecretKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [showValue, setShowValue] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const { getToken } = useAuth();

  useEffect(() => {
    if (isOpen && appId) {
      fetchSecretKeys();
      // Reset form when modal opens
      setShowAddForm(false);
      setNewKey('');
      setNewValue('');
      setShowValue(false);
      setAddError(null);
    }
  }, [isOpen, appId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSecretKeys = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`/api/applications/${appId}/vault`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch vault keys');
      }

      const data = await response.json();
      setSecretKeys(data.keys || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vault keys');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSecret = async () => {
    if (!newKey || !newValue) return;

    setAdding(true);
    setAddError(null);

    try {
      const token = await getToken();
      const response = await fetch(`/api/applications/${appId}/vault`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          key: newKey,
          value: newValue,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to add secret');
      }

      // Reset form and refresh list
      setNewKey('');
      setNewValue('');
      setShowValue(false);
      setShowAddForm(false);
      await fetchSecretKeys();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add secret');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteSecret = async (key: string) => {
    if (!window.confirm(`Are you sure you want to delete the secret "${key}"?`)) {
      return;
    }

    setDeletingKey(key);
    setAddError(null);

    try {
      const token = await getToken();
      const response = await fetch(`/api/applications/${appId}/vault/${encodeURIComponent(key)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete secret');
      }

      // Refresh the list
      await fetchSecretKeys();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to delete secret');
    } finally {
      setDeletingKey(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">🔒 Vault Secrets</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading secrets...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchSecretKeys}
                className="mt-4 text-sm text-blue-600 hover:text-blue-700"
              >
                Try again
              </button>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              {addError && (
                <div className="m-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                  {addError}
                </div>
              )}
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-[45%]" />
                  <col className="w-[45%]" />
                  <col className="w-[10%]" />
                </colgroup>
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Key</th>
                    <th className="text-left px-4 py-2 text-sm font-medium text-gray-700">Value</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {secretKeys.length === 0 && !showAddForm && (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                        <p>No secrets found in vault</p>
                        <p className="text-sm text-gray-400 mt-1">
                          Click "Add Secret" below to add one
                        </p>
                      </td>
                    </tr>
                  )}
                  {secretKeys.map((key, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-mono">{key}</td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-400">••••••••••••</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDeleteSecret(key)}
                          disabled={deletingKey === key}
                          className="text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete secret"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {showAddForm ? (
                    <tr className="bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={newKey}
                          onChange={(e) => setNewKey(e.target.value)}
                          placeholder="e.g., stripe_api_key"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-black"
                          disabled={adding}
                          autoComplete="off"
                          autoFocus
                        />
                      </td>
                      <td className="px-4 py-3" colSpan={2}>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              value={newValue}
                              onChange={(e) => setNewValue(e.target.value)}
                              placeholder="Secret value"
                              className="w-full px-2 py-1 pr-8 border border-gray-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-black"
                              style={showValue ? {} : { WebkitTextSecurity: 'disc' } as React.CSSProperties}
                              disabled={adding}
                              autoComplete="new-password"
                              data-1p-ignore
                            />
                            <button
                              type="button"
                              onClick={() => setShowValue(!showValue)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              disabled={adding}
                            >
                              {showValue ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <Button
                            onClick={handleAddSecret}
                            disabled={adding || !newKey || !newValue}
                            size="sm"
                            className="px-6 flex-shrink-0"
                          >
                            {adding ? 'Setting...' : 'Set'}
                          </Button>
                          <button
                            onClick={() => {
                              setShowAddForm(false);
                              setNewKey('');
                              setNewValue('');
                              setShowValue(false);
                              setAddError(null);
                            }}
                            disabled={adding}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                            title="Cancel"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr className="hover:bg-gray-50">
                      <td colSpan={3} className="px-4 py-3">
                        <button
                          onClick={() => setShowAddForm(true)}
                          className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                          <Plus className="h-4 w-4" />
                          Add Secret
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
